import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  alpha,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';

interface Zone {
  id: number;
  name: string;
  minValue: number;
  maxValue: number;
  description?: string;
}

interface PhysiologicalMarker {
  label: string;
  value: number;
  tooltip: string;
}

interface ZonesTableProps {
  zones: Zone[];
  zoneType: 'heartRate' | 'pace';
  unit?: string;
  onSave: (zones: Zone[]) => void;
  editable?: boolean;
  maxHr?: number;
  lactateThresholdHr?: number;
}

/**
 * ZonesTable Component
 * 
 * Displays and allows editing of heart rate or pace zones in a table format.
 * Supports inline editing with validation and save/cancel functionality.
 * Now includes physiological markers (LT1, LT2/LTHR, VO₂max HR) for heart rate zones.
 * 
 * @param zones - Array of zone objects to display
 * @param zoneType - Type of zones ('heartRate' or 'pace')
 * @param unit - Unit of measurement (e.g., 'bpm', 'min/km')
 * @param onSave - Callback function when zones are saved
 * @param editable - Whether the table allows editing (default: true)
 * @param maxHr - Maximum heart rate for calculating markers
 * @param lactateThresholdHr - Lactate threshold heart rate
 */
const ZonesTable: React.FC<ZonesTableProps> = ({
  zones,
  zoneType,
  unit = zoneType === 'heartRate' ? 'bpm' : 'min/km',
  onSave,
  editable = true,
  maxHr,
  lactateThresholdHr,
}) => {

  const [isEditing, setIsEditing] = useState(false);
  const [editedZones, setEditedZones] = useState<Zone[]>(zones);
  const [errors, setErrors] = useState<Record<number, string>>({});

  // Update editedZones when zones prop changes
  React.useEffect(() => {
    setEditedZones(zones);
  }, [zones]);

  // Get improved zone names and descriptions
  const getZoneInfo = (zoneId: number): { name: string; description: string } => {
    if (zoneType === 'heartRate') {
      const zoneInfo: Record<number, { name: string; description: string }> = {
        1: {
          name: 'Recovery / Easy Aerobic',
          description: 'Very low-intensity aerobic work. Below LT1. Minimal lactate accumulation, fast recovery, promotes blood flow and basic aerobic efficiency.'
        },
        2: {
          name: 'Aerobic Endurance',
          description: 'Fully aerobic training just below LT1. Fat-dominant metabolism, high mitochondrial stimulus, sustainable for long durations. Core endurance base.'
        },
        3: {
          name: 'Moderate / Tempo',
          description: 'Between LT1 and LT2. Increasing carbohydrate use and lactate production. Sustainable but costly; useful for tempo efforts, limited for high volume.'
        },
        4: {
          name: 'Threshold (Near LT2)',
          description: 'High-intensity aerobic work just below LT2. Lactate production is high but controlled. Strong predictor of endurance performance.'
        },
        5: {
          name: 'VO₂max / Maximal',
          description: 'Above LT2, approaching VO₂max. Oxygen uptake near maximum. Very high lactate accumulation, only sustainable for short intervals.'
        }
      };
      return zoneInfo[zoneId] || { name: `Zone ${zoneId}`, description: `Zone ${zoneId} intensity` };
    }
    // Keep original names for pace zones
    return { name: zones.find(z => z.id === zoneId)?.name || `Zone ${zoneId}`, description: zones.find(z => z.id === zoneId)?.description || '' };
  };

  // Calculate physiological markers for heart rate zones
  const getPhysiologicalMarkers = (): (PhysiologicalMarker & { color: string })[] => {
    if (zoneType !== 'heartRate' || zones.length < 5) return [];

    const markers: (PhysiologicalMarker & { color: string })[] = [];

    // LT1 - Boundary between Z2 and Z3
    const zone2 = zones.find(z => z.id === 2);
    const zone3 = zones.find(z => z.id === 3);
    if (zone2 && zone3) {
      markers.push({
        label: 'LT1',
        value: zone2.maxValue, // or zone3.minValue, should be the same
        tooltip: 'Aerobic Threshold — Intensity where lactate first rises above baseline. Marks transition from easy aerobic to moderate intensity.',
        color: '#4ade80' // Green
      });
    }

    // LT2/LTHR - Boundary between Z4 and Z5
    const zone4 = zones.find(z => z.id === 4);
    const zone5 = zones.find(z => z.id === 5);
    if (zone4 && zone5) {
      markers.push({
        label: 'LT2 / LTHR',
        value: lactateThresholdHr || zone4.maxValue, // Use provided LTHR or boundary
        tooltip: 'Lactate Threshold — Highest sustainable steady-state intensity. Above this point, lactate accumulates rapidly.',
        color: '#fb923c' // Orange
      });
    }

    // VO₂max HR - Upper end of Z5
    if (zone5) {
      markers.push({
        label: 'VO₂max HR',
        value: maxHr || zone5.maxValue,
        tooltip: 'VO₂max Heart Rate — Heart rate associated with maximal oxygen uptake. Represents the upper physiological ceiling.',
        color: '#ef4444' // Red
      });
    }

    return markers;
  };

  const physiologicalMarkers = getPhysiologicalMarkers();

  const handleEdit = () => {
    setIsEditing(true);
    setEditedZones([...zones]);
    setErrors({});
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedZones([...zones]);
    setErrors({});
  };

  const validateZones = (zonesToValidate: Zone[]): boolean => {
    const newErrors: Record<number, string> = {};
    let isValid = true;

    zonesToValidate.forEach((zone, index) => {
      if (isNaN(zone.minValue) || isNaN(zone.maxValue)) {
        newErrors[zone.id] = 'Values must be numbers';
        isValid = false;
        return;
      }

      if (zone.minValue >= zone.maxValue) {
        newErrors[zone.id] = 'Min must be less than max';
        isValid = false;
        return;
      }

      if (index > 0) {
        const prevZone = zonesToValidate[index - 1];
        if (zone.minValue < prevZone.maxValue) {
          newErrors[zone.id] = 'Zones cannot overlap';
          isValid = false;
        }
      }

      if (zone.minValue < 0 || zone.maxValue < 0) {
        newErrors[zone.id] = 'Values must be positive';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = () => {
    if (validateZones(editedZones)) {
      onSave(editedZones);
      setIsEditing(false);
      setErrors({});
    }
  };

  const handleZoneChange = (zoneId: number, field: 'minValue' | 'maxValue', value: string) => {
    const numValue = parseFloat(value);
    setEditedZones((prev) =>
      prev.map((zone) =>
        zone.id === zoneId
          ? { ...zone, [field]: isNaN(numValue) ? 0 : numValue }
          : zone
      )
    );
  };

  const getZoneColor = (index: number): string => {
    const colors = [
      '#4ade80', // Zone 1 - Light Green
      '#22c55e', // Zone 2 - Green
      '#facc15', // Zone 3 - Yellow
      '#fb923c', // Zone 4 - Orange
      '#ef4444', // Zone 5 - Red
    ];
    return colors[index % colors.length];
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
            {zoneType === 'heartRate' ? 'Heart Rate Zones' : 'Pace Zones'}
            <Chip
              label={unit}
              size="small"
              sx={{ fontWeight: 600, height: 20, fontSize: '0.65rem', bgcolor: 'action.hover' }}
            />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {zoneType === 'heartRate'
              ? 'Zones are continuous HR bands with physiological transitions'
              : 'Target intensities for different workout types'}
          </Typography>
        </Box>
        {editable && !isEditing && (
          <Button
            startIcon={<EditIcon />}
            onClick={handleEdit}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Adjust
          </Button>
        )}
        {isEditing && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<SaveIcon />}
              onClick={handleSave}
              variant="contained"
              size="small"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Save
            </Button>
            <Button
              onClick={handleCancel}
              variant="outlined"
              size="small"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      {/* Physiological Markers - Left Axis */}
      {zoneType === 'heartRate' && physiologicalMarkers.length > 0 && (
        <Box sx={{ mb: 2, pl: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, display: 'block' }}>
            Physiological Markers
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {physiologicalMarkers.map((marker, idx) => (
              <Tooltip key={idx} title={marker.tooltip} arrow placement="left">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'help',
                    '&:hover': {
                      '& .marker-line': {
                        bgcolor: marker.color,
                        opacity: 1,
                      },
                      '& .marker-label': {
                        color: marker.color,
                        fontWeight: 700,
                      }
                    }
                  }}
                >
                  <Typography
                    className="marker-label"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      minWidth: 80,
                      color: marker.color,
                      transition: 'all 0.2s ease',
                      opacity: 0.9
                    }}
                  >
                    {marker.label}
                  </Typography>
                  <Box
                    className="marker-line"
                    sx={{
                      height: 2,
                      flex: 1,
                      bgcolor: alpha(marker.color, 0.3),
                      borderRadius: 1,
                      transition: 'all 0.2s ease',
                      opacity: 0.6,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      minWidth: 50,
                      textAlign: 'right',
                      color: 'text.primary'
                    }}
                  >
                    {marker.value.toFixed(0)} {unit}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      <TableContainer
        sx={{
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible'
        }}
      >
        <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
          <TableBody>
            {editedZones.map((zone, index) => {
              const zoneInfo = getZoneInfo(zone.id);
              return (
                <TableRow
                  key={zone.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    bgcolor: (theme) => alpha(getZoneColor(index), theme.palette.mode === 'dark' ? 0.08 : 0.04),
                    '&:hover': {
                      bgcolor: (theme) => alpha(getZoneColor(index), theme.palette.mode === 'dark' ? 0.12 : 0.08),
                    },
                    transition: 'background-color 0.2s ease',
                    borderRadius: 3,
                    '& td:first-of-type': { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 },
                    '& td:last-of-type': { borderTopRightRadius: 12, borderBottomRightRadius: 12 },
                  }}
                >
                  <TableCell sx={{ width: 60 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: getZoneColor(index),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 800,
                        fontSize: '0.875rem',
                        boxShadow: `0 4px 12px ${getZoneColor(index)}40`,
                      }}
                    >
                      Z{zone.id}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>
                    <Tooltip title={zoneInfo.description} arrow placement="top">
                      <Box sx={{ cursor: 'help' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.2 }}>
                          {zoneInfo.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
                          {zoneInfo.description}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <TextField
                            value={zone.minValue}
                            onChange={(e) => handleZoneChange(zone.id, 'minValue', e.target.value)}
                            size="small"
                            error={!!errors[zone.id]}
                            inputProps={{ style: { textAlign: 'right', padding: '4px 8px' } }}
                            sx={{
                              width: 70,
                              '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.875rem' }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">—</Typography>
                          <TextField
                            value={zone.maxValue}
                            onChange={(e) => handleZoneChange(zone.id, 'maxValue', e.target.value)}
                            size="small"
                            error={!!errors[zone.id]}
                            inputProps={{ style: { textAlign: 'right', padding: '4px 8px' } }}
                            sx={{
                              width: 70,
                              '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.875rem' }
                            }}
                          />
                        </>
                      ) : (
                        <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                          {zone.minValue.toFixed(zoneType === 'heartRate' ? 0 : 2)} - {zone.maxValue.toFixed(zoneType === 'heartRate' ? 0 : 2)}
                        </Typography>
                      )}

                    </Box>
                    {errors[zone.id] && (
                      <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem', display: 'block', textAlign: 'right' }}>
                        {errors[zone.id]}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ZonesTable;